import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ValueType = {
  type: 'projectConnect' | 'projectUpdate';
  resourceId: string;
  contentId?: string;
};

export interface CornerDialogContextInterface {
  values: ValueType[];
  add: (value: ValueType) => void;
  remove: (value: ValueType) => void;
}

const CornerDialogContext = createContext<CornerDialogContextInterface>({
  values: [],
  add: () => {},
  remove: () => {},
});

export const CornerDialogProvider: FC<PropsWithChildren> = ({ children }) => {
  const [values, setValues] = useState<ValueType[]>([]);

  const add: CornerDialogContextInterface['add'] = useCallback((value) => {
    setValues((prev) => [
      ...prev.filter(
        (p) => !(p.type === value.type && p.resourceId === value.resourceId),
      ),
      value,
    ]);
  }, []);

  const remove: CornerDialogContextInterface['remove'] = useCallback(
    (value) => {
      setValues((prev) =>
        prev.filter(
          (p) => !(p.type === value.type && p.resourceId === value.resourceId),
        ),
      );
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      values,
      add,
      remove,
    }),
    [values, add, remove],
  );

  return (
    <CornerDialogContext.Provider value={contextValue}>
      {children}
    </CornerDialogContext.Provider>
  );
};

export const useCornerDialogContext = (): CornerDialogContextInterface =>
  useContext(CornerDialogContext);
